import {chain} from 'lodash';
import {createSelector} from 'reselect';

import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {sortConversations} from '../../../src/helpers/models/conversationHelpers';
import {AppParametricSelector, wrapInShallowSelector} from '../../../src/helpers/reselectHelpers';
import {ConversationModel} from '../../../src/models/api/conversationModel';
import {ConversationOverlayModel} from '../../../src/models/api/conversationOverlayModel';
import {ResourceMap} from '../../../src/store/appTypes';
import {getConversations} from '../../../src/store/data/dataSelectors';
import {CellsPanelConversationsPageState} from '../../../src/store/panels/cells/cellsPanelState';
import {InboxesConversationsParameters} from '../../../src/store/panels/inboxes/parameters/inboxesParameters';
import {computeIsIdSelected} from '../../../src/store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildIndexMap, ItemIndexes} from '../../helpers/itemIndexHelpers';
import {reportToBugsnag} from '../../services/bugsnag/bugsnag';

/*
 * Types.
 */

type GetPage<P> = AppParametricSelector<P, CellsPanelConversationsPageState>;
type GetConversations<P> = AppParametricSelector<P, ReadonlyArray<ConversationModel>>;

export interface CellsConversationsContent {
  conversations: ReadonlyArray<ConversationModel>;
  conversationIds: ReadonlyArray<number>;
  conversationIndexes: ItemIndexes;
  extraConversationIds: ReadonlySet<number>;
  hiddenConversationId: number | null;
  conversationOverlays: ResourceMap<ConversationOverlayModel>;
}

export type ConversationDateMap = {[T: number]: number};

/*
 * Selectors.
 */

export function makeGetCellsContent<P>(getPage: GetPage<P>) {
  const getExtraConversations = makeGetExtraConversations(getPage);
  const getPageConversations = wrapInShallowSelector(
    makeGetPageConversations(getPage, getExtraConversations),
  );
  const getHiddenConversationId = createSelector(getPage, (page) => page.hiddenConversationId);
  const getSortedConversations = createSelector([getPage, getPageConversations], (page, pageConversations) =>
    sortConversations(page.cellsContext, page.parameters, pageConversations),
  );
  const getSortedConversationIds = wrapInShallowSelector(
    createSelector(getSortedConversations, (sortedConversations) => sortedConversations.map((c) => c.id)),
  );
  const getConversationIndexes = createSelector(getSortedConversationIds, (sortedConversationIds) =>
    buildIndexMap(sortedConversationIds),
  );

  const getConversationOverlays = makeGetConversationOverlays(getPage);

  return createSelector(
    [
      getSortedConversations,
      getSortedConversationIds,
      getConversationIndexes,
      getExtraConversations,
      getHiddenConversationId,
      getConversationOverlays,
    ],
    (
      sortedConversations,
      sortedConversationIds,
      conversationIndexes,
      extraConversations,
      hiddenConversationId,
      conversationOverlays,
    ) => {
      const extraConversationIds = new Set(extraConversations.map((c) => c.id));

      // Otherwise, gather the various properties.
      const result: CellsConversationsContent = {
        conversations: sortedConversations,
        conversationIds: sortedConversationIds,
        conversationIndexes,
        extraConversationIds,
        hiddenConversationId: hiddenConversationId || null,
        conversationOverlays,
      };
      return result;
    },
  );
}

function makeGetExtraConversations<P>(getPage: GetPage<P>) {
  return createSelector([getPage, getConversations], (page, conversations) =>
    chain(page.removedConversationIds)
      .filter((id) => computeIsIdSelected(page.parameters.selection, id))
      .difference(page.conversationIds)
      .map((id) => conversations[id])
      .compact()
      .filter((c) => Boolean(page.conversationOverlays[c.id]))
      .value(),
  );
}

function makeGetPageConversations<P>(getPage: GetPage<P>, getExtraConversations: GetConversations<P>) {
  return createSelector(
    [getPage, getExtraConversations, getConversations],
    (page, extraConversations, conversations) =>
      chain(page.conversationIds)
        .map((id) => {
          const conversation = conversations[id];

          if (!conversation) {
            reportMissingConversation(page.parameters, id);
          }

          return conversation;
        })
        .compact()
        .concat(extraConversations)
        .uniqBy((conversation) => conversation.id)
        .value(),
  );
}

function makeGetConversationOverlays<P>(getPage: GetPage<P>) {
  return createSelector(getPage, (page) => page.conversationOverlays);
}

const reportedMissingConversationIds = new Set<number>();

function reportMissingConversation(parameters: InboxesConversationsParameters, id: number) {
  // Report only once for each conversation ID for the lifetime of the app.
  if (reportedMissingConversationIds.has(id)) {
    return;
  }

  reportedMissingConversationIds.add(id);

  reportToBugsnag(
    new ErrorWithData('Detected P2 conversation missing', {
      parameters,
      conversationId: id,
    }),
  );
}
